import React, { useState } from 'react';
import { apiClient, useAuth } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Zap, Send, MessageCircle, AlertTriangle, Target, Lightbulb,
  Search, FileText, BookOpen, Phone
} from 'lucide-react';

const SENTIMENT_COLORS = {
  interested: 'bg-green-500/10 text-green-600',
  ready_to_close: 'bg-emerald-500/10 text-emerald-600',
  hesitant: 'bg-yellow-500/10 text-yellow-600',
  frustrated: 'bg-red-500/10 text-red-600',
  unknown: 'bg-muted text-foreground',
};

export default function RealtimeAssistPage() {
  const { user } = useAuth();
  const [input, setInput] = useState('');
  const [leadId, setLeadId] = useState('');
  const [assists, setAssists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  async function getAssist() {
    if (!input.trim()) return;
    setLoading(true);
    const userInput = input;
    setInput('');
    try {
      const res = await apiClient.post('/calls/realtime-assist', {
        message: userInput,
        lead_id: leadId || undefined,
      }, { timeout: 15000 });
      setAssists(prev => [{ input: userInput, ...res.data, time: new Date().toISOString() }, ...prev]);
    } catch {
      toast.error('Assist failed — try again');
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch() {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await apiClient.get(`/calls/search?q=${encodeURIComponent(searchQuery)}&limit=8`);
      setSearchResults(res.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  const typeIcons = { transcript: Phone, objection: AlertTriangle, knowledge_base: BookOpen };

  return (
    <div className="space-y-6" data-testid="realtime-assist-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
          <Zap className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Real-Time Call Assist</h1>
          <p className="text-muted-foreground text-sm">Type what the customer just said — get instant rebuttals and suggestions</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Assist Panel */}
        <div className="lg:col-span-2 space-y-4">
          {/* Input */}
          <Card className="border-primary/30">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <Input
                  placeholder="Optional: Lead ID for context"
                  value={leadId}
                  onChange={e => setLeadId(e.target.value)}
                  className="max-w-xs h-8 text-xs"
                  data-testid="lead-id-input"
                />
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder='Type what the customer just said... e.g. "This is too expensive for me"'
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && getAssist()}
                  className="text-base"
                  data-testid="assist-input"
                />
                <Button onClick={getAssist} disabled={loading || !input.trim()} data-testid="get-assist-btn">
                  {loading ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Assist Results */}
          {assists.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Zap className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
                <h3 className="text-lg font-semibold mb-1">Ready to Assist</h3>
                <p className="text-muted-foreground text-sm">Type what your customer is saying and get instant AI-powered rebuttals and coaching tips.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {assists.map((a, i) => (
                <Card key={i} className="border-l-4 border-l-primary" data-testid={`assist-result-${i}`}>
                  <CardContent className="py-4 space-y-3">
                    {/* Customer said */}
                    <div className="flex items-start gap-2">
                      <MessageCircle className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <p className="text-sm text-muted-foreground italic">"{a.input}"</p>
                    </div>

                    {/* Quick Response */}
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                      <p className="text-xs font-medium text-primary mb-1">Say This Now:</p>
                      <p className="text-sm font-medium">{a.quick_response}</p>
                    </div>

                    {/* Objection + Rebuttal */}
                    {a.objection_detected && (
                      <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                          <Badge className="bg-orange-500/10 text-orange-600 text-xs">{a.objection_detected} objection</Badge>
                        </div>
                        {a.rebuttal && <p className="text-sm">{a.rebuttal}</p>}
                      </div>
                    )}

                    {/* Tips + Sentiment */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex gap-2 flex-wrap">
                        {a.tips?.map((t, j) => (
                          <Badge key={j} variant="outline" className="text-xs gap-1">
                            <Lightbulb className="h-3 w-3" />{t}
                          </Badge>
                        ))}
                      </div>
                      {a.sentiment_hint && (
                        <Badge className={SENTIMENT_COLORS[a.sentiment_hint] || SENTIMENT_COLORS.unknown}>
                          Customer: {a.sentiment_hint?.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>

                    {/* Close Suggestion */}
                    {a.suggested_close && (
                      <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Target className="h-3.5 w-3.5 text-green-600" />
                          <span className="text-xs font-medium text-green-600">Closing Opportunity</span>
                        </div>
                        <p className="text-sm">{a.suggested_close}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Semantic Search Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Search className="h-4 w-4" /> Knowledge Search</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  placeholder="Search transcripts, scripts..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  data-testid="search-input"
                />
                <Button size="icon" onClick={handleSearch} disabled={searching}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {searchResults.length > 0 && (
            <ScrollArea className="max-h-[600px]">
              <div className="space-y-2">
                {searchResults.map((r, i) => {
                  const Icon = typeIcons[r.type] || FileText;
                  return (
                    <Card key={i} className="cursor-pointer hover:border-primary/30 transition-colors" data-testid={`search-result-${i}`}>
                      <CardContent className="py-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          <Badge variant="outline" className="text-[10px]">{r.type}</Badge>
                        </div>
                        <p className="text-sm font-medium line-clamp-1">{r.title}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{r.snippet}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  );
}
